import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faClock, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const bucketOrder = ['0-7 days', '8-14 days', '15-30 days', '31-60 days', '61-90 days', '90+ days']

const bucketLabels = {
  '0-7 days': 'Fast track',
  '8-14 days': 'Quick',
  '15-30 days': 'Standard',
  '31-60 days': 'Slow',
  '61-90 days': 'Very slow',
  '90+ days': 'Longest'
}

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const formatDate = (value) => {
  if (!value) return '—'
  const date = value.toDate ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const toDateValue = (value) => {
  if (!value) return null
  if (typeof value.toDate === 'function') {
    const converted = value.toDate()
    return Number.isNaN(converted.getTime()) ? null : converted
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDurationLabel = (days, diffMs) => {
  if (days <= 0) {
    const diffHours = Math.max(Math.floor(diffMs / (1000 * 60 * 60)), 0)
    const diffMinutes = Math.max(Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)), 0)
    return `${diffHours}h ${diffMinutes}m`
  }

  return `${days} day${days === 1 ? '' : 's'}`
}

const getBucket = (diffDays) => {
  if (diffDays <= 7) return '0-7 days'
  if (diffDays <= 14) return '8-14 days'
  if (diffDays <= 30) return '15-30 days'
  if (diffDays <= 60) return '31-60 days'
  if (diffDays <= 90) return '61-90 days'
  return '90+ days'
}

const ApplicationTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="applicationAdmTooltip">
      <strong>{label}</strong>
      <div className="applicationAdmTooltipRow">
        <span>Records</span>
        <strong>{formatCount(row.count)}</strong>
      </div>
      <div className="applicationAdmTooltipRow">
        <span>Bucket</span>
        <strong>{bucketLabels[row.range] || row.range}</strong>
      </div>
    </div>
  )
}

function ApplicationToAdmTime() {
  const db = getFirestore(app)
  const [chartData, setChartData] = useState([])
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const snapshot = await getDocs(collection(db, 'users'))
      const bucketCounts = bucketOrder.reduce((accumulator, bucket) => {
        accumulator[bucket] = 0
        return accumulator
      }, {})
      const tableRows = []
      const filterStart = new Date('2024-06-01')

      snapshot.forEach((docItem) => {
        const data = docItem.data()
        const appDate = toDateValue(data.appDate)
        const admissionDate = toDateValue(data.admissionDate)

        if (!appDate || !admissionDate) {
          return
        }

        if (appDate < filterStart) {
          return
        }

        const diffMs = admissionDate.getTime() - appDate.getTime()
        const diffDays = Math.max(Math.floor(diffMs / (1000 * 60 * 60 * 24)), 0)
        const bucket = getBucket(diffDays)
        bucketCounts[bucket] += 1

        tableRows.push({
          id: docItem.id,
          name: data.fullName || data.bio || data.displayName || 'Unnamed student',
          appDate,
          admissionDate,
          durationDays: diffDays,
          durationLabel: formatDurationLabel(diffDays, diffMs),
          bucket
        })
      })

      const formattedData = bucketOrder.map((range) => ({
        range,
        label: bucketLabels[range] || range,
        count: bucketCounts[range]
      }))

      tableRows.sort((first, second) => {
        const secondTime = second.admissionDate?.getTime?.() || 0
        const firstTime = first.admissionDate?.getTime?.() || 0
        if (secondTime !== firstTime) {
          return secondTime - firstTime
        }

        return String(first.name || '').localeCompare(String(second.name || ''))
      })

      setChartData(formattedData)
      setTableData(tableRows)
    } catch (fetchError) {
      console.error('Failed to load application to admission time:', fetchError)
      setChartData([])
      setTableData([])
      setError('We could not load the application-to-admission time data right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredTable = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()
    if (!search) {
      return tableData
    }

    return tableData.filter((row) => row.name.toLowerCase().includes(search))
  }, [searchTerm, tableData])

  const stats = useMemo(() => {
    const summary = {
      records: tableData.length,
      totalDays: 0,
      fastTrack: 0,
      slowTrack: 0
    }

    tableData.forEach((row) => {
      summary.totalDays += row.durationDays
      if (row.durationDays <= 30) {
        summary.fastTrack += 1
      } else {
        summary.slowTrack += 1
      }
    })

    return summary
  }, [tableData])

  const averageDays = stats.records > 0 ? stats.totalDays / stats.records : 0
  const fastestRecord = tableData.reduce((current, row) => {
    if (!current) return row
    return row.durationDays < current.durationDays ? row : current
  }, null)
  const slowestRecord = tableData.reduce((current, row) => {
    if (!current) return row
    return row.durationDays > current.durationDays ? row : current
  }, null)
  const topBuckets = chartData.slice(0, 3)
  const hasRows = filteredTable.length > 0
  const emptyMessage = tableData.length
    ? 'No records match the current search.'
    : 'No application-to-admission records were found for June 2024 onward.'

  return (
    <div className="mainContainer applicationAdmTimePage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="applicationAdmTimeContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Application to Admission Time</h4>
              <p className="appsSubtext">
                Track how long students take to move from application to admission, and see which time buckets carry the most volume.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Records</span>
                <strong>{formatCount(stats.records)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Average days</span>
                <strong>{averageDays ? averageDays.toFixed(1) : '0.0'}</strong>
              </div>
              <div className="appsStatChip">
                <span>Fast track</span>
                <strong>{formatCount(stats.fastTrack)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Slow track</span>
                <strong>{formatCount(stats.slowTrack)}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard applicationAdmFiltersCard">
            <div className="appsSearch input-group applicationAdmSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search student name..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="applicationAdmFiltersMeta">
              <span>{formatCount(filteredTable.length)} records visible</span>
              <span>{formatCount(chartData.length)} bucket groups in the chart</span>
            </div>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="applicationAdmGrid">
            <div className="appsTableCard applicationAdmChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                  Processing time buckets
                </h6>
                <span>June 2024 onward</span>
              </div>
              <div className="applicationAdmChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading application to admission time...</span>
                    </Spinner>
                    <p>Loading application to admission time...</p>
                  </div>
                ) : chartData.length ? (
                  <div className="applicationAdmChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                        <XAxis
                          dataKey="range"
                          tick={{ fontSize: 12, fill: '#475569' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <Tooltip content={<ApplicationTooltip />} />
                        <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                        <Bar dataKey="count" name="Applications" fill="#0f766e" radius={[10, 10, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>{emptyMessage}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="appsInsightsCard applicationAdmInsightsCard">
              <div className="appsInsightsHeader">
                <h6>
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  Time profile
                </h6>
                <span>{formatCount(topBuckets.length)} buckets shown</span>
              </div>

              <div className="appsInsightsList">
                {topBuckets.length ? (
                  topBuckets.map((row) => (
                    <div className="appsInsightsItem" key={row.range}>
                      <div>
                        <strong>{row.label}</strong>
                        <span>{row.range}</span>
                      </div>
                      <small>{formatCount(row.count)} records</small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <p>No time buckets available yet.</p>
                  </div>
                )}
              </div>

              <div className="applicationAdmMiniStats">
                <div className="applicationAdmMiniStat">
                  <span>Fastest</span>
                  <strong>{fastestRecord ? fastestRecord.durationLabel : '—'}</strong>
                  <small>{fastestRecord ? fastestRecord.name : 'No record'}</small>
                </div>
                <div className="applicationAdmMiniStat">
                  <span>Longest</span>
                  <strong>{slowestRecord ? slowestRecord.durationLabel : '—'}</strong>
                  <small>{slowestRecord ? slowestRecord.name : 'No record'}</small>
                </div>
              </div>

              <p className="appsInsightsMore">
                Search a student name to narrow the table, or refresh after new applications are approved.
              </p>
            </div>
          </div>

          <div className="appsTableCard applicationAdmTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Detailed records
              </h6>
              <span>{formatCount(filteredTable.length)} results</span>
            </div>
            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading detailed records...</span>
                  </Spinner>
                  <p>Loading detailed records...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable applicationAdmTable">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Application Date</th>
                      <th>Admission Date</th>
                      <th>Duration</th>
                      <th>Bucket</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasRows ? (
                      filteredTable.map((row) => {
                        const progress = Math.min(((row.durationDays + 1) / 90) * 100, 100)

                        return (
                          <tr key={row.id}>
                            <td>
                              <div className="applicationAdmNameCell">
                                <strong>{row.name}</strong>
                                <span>{row.durationDays <= 7 ? 'Very fast conversion' : row.durationDays <= 30 ? 'Moderate conversion time' : 'Extended conversion time'}</span>
                              </div>
                            </td>
                            <td>{formatDate(row.appDate)}</td>
                            <td>{formatDate(row.admissionDate)}</td>
                            <td>{row.durationLabel}</td>
                            <td>
                              <span className="applicationAdmBucketBadge">
                                {bucketLabels[row.bucket] || row.bucket}
                              </span>
                            </td>
                            <td>
                              <div className="applicationAdmTrack" aria-hidden="true">
                                <span className="applicationAdmTrackFill" style={{ width: `${progress}%` }} />
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApplicationToAdmTime
